import { Alert, Button, Card, CardContent, CardHeader } from '@mui/material';
import { FormProvider, FormSubmitButton, FormText } from 'components/form';
import { getInvites, updateUserLastLoginPortal } from 'lib/firebase';
import { noop } from 'lodash';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { StoredUser } from 'store/features/authentication';
import * as yup from 'yup';
import { getAuth, updatePassword } from 'firebase/auth';
import styles from './Invite.module.scss';
import { Box } from '@mui/system';
import { BaseModal } from 'components/modals/BaseModal/BaseModal';
import { LoadingSpinner } from 'components/data/LoadingSpinner';
import { Logo } from 'lib/themes';

type PasswordInput = {
    password: string;
    confirmPassword: string;
};

const defaultPasswordValues: PasswordInput = {
    password: '',
    confirmPassword: '',
};

const passwordSchema = yup.object({
    password: yup.string().required(),
    confirmPassword: yup
        .string()
        .required()
        .oneOf([yup.ref('password')], 'Passwords must match'),
});

interface NewUserInviteProps {
    setTosDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
export const NewUserInvite = ({ setTosDialogOpen }: NewUserInviteProps) => {
    const location = useLocation();
    const [inviteCode, setInviteCode] = useState('');
    const [invalidError, setInvalidError] = useState('');
    const [userInfo, setUserInfo] = useState<StoredUser | null>(null);
    const [modalOpen, setModalOpen] = useState(true);

    const firebaseAuth = getAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const newInviteCode = location?.pathname?.split('/')?.at(2);
        if (newInviteCode) {
            setInviteCode(newInviteCode);
        }
    }, [location]);

    useEffect(() => {
        if (inviteCode) {
            getInvites(inviteCode)
                .then((result) => {
                    setUserInfo(result);
                })
                .catch(() => {
                    setInvalidError('Login token invalid');
                });
        }
    }, [inviteCode]);

    const onSubmit = (data: PasswordInput) => {
        if (
            data.password === data.confirmPassword &&
            firebaseAuth?.currentUser &&
            userInfo?.uid
        ) {
            updatePassword(firebaseAuth.currentUser, data.password).then(() => {
                if (userInfo.uid && userInfo.protected?.supplierId) {
                    updateUserLastLoginPortal(
                        userInfo.uid,
                        userInfo.protected?.supplierId
                    ).then(() => {
                        navigate('/accounts');
                        setTosDialogOpen(true);
                    });
                }
            });
        }
    };

    const handleNavigateLanding = () => {
        setModalOpen(false);
        navigate('/');
    };

    return (
        <BaseModal open={modalOpen} maxWidth="xs" data-testid="NewUserInvite">
            <Card className={styles.card}>
                <Logo className={styles.logo} />
                {!inviteCode && (
                    <Alert className={styles.alert} severity="error">
                        Invite code not found, please reach out to an
                        administrator if this problem persists.
                    </Alert>
                )}
                {invalidError && (
                    <Alert className={styles.alert} severity="error">
                        {invalidError}, please reach out to an administrator if
                        this problem persists.
                    </Alert>
                )}
                {!!userInfo && userInfo.uid && !invalidError ? (
                    <>
                        <CardHeader title="Please create a password"></CardHeader>
                        <CardContent>
                            <FormProvider
                                defaultValues={defaultPasswordValues}
                                validationSchema={passwordSchema}
                            >
                                <Box className={styles.formContainer}>
                                    <FormText field="password" textSecurity />
                                    <FormText
                                        field="confirmPassword"
                                        textSecurity
                                    />
                                    <FormSubmitButton
                                        onValidHandler={onSubmit}
                                        onErrorHandler={noop}
                                    />
                                </Box>
                            </FormProvider>
                        </CardContent>
                    </>
                ) : !invalidError && inviteCode ? (
                    <LoadingSpinner />
                ) : null}
                <Button onClick={handleNavigateLanding}>Back to login</Button>
            </Card>
        </BaseModal>
    );
};
